import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../api";
import { useAuth } from "../AuthContext";
import { PageHeader, EmptyState, Modal, Field, Button } from "../components/UI";
import { MapPin, Plus, Calendar, Check, Trash2, Phone, X, Sparkles, Navigation, RefreshCw } from "lucide-react";
import Typeahead from "../components/Typeahead";

const searchCustomersAsync = async (q) => {
  const url = q ? `/customers?q=${encodeURIComponent(q)}&limit=50` : "/customers?limit=50";
  const r = await api.get(url);
  return r.data;
};
import { toast } from "sonner";

const statusMeta = {
  planned: { cls: "bg-blue-100 text-blue-800", label: "Planned" },
  done: { cls: "bg-green-100 text-green-800", label: "Done ✓" },
  missed: { cls: "bg-red-100 text-red-700", label: "Missed" },
  cancelled: { cls: "bg-slate-100 text-slate-600", label: "Cancelled" },
};

const blank = { customer_id: "", visit_date: "", visit_time: "", purpose: "", notes: "", assigned_to: "" };

function isoToday() { return new Date().toISOString().slice(0, 10); }
function isoMonth() { return new Date().toISOString().slice(0, 7); }

export default function VisitPlanner() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [month, setMonth] = useState(isoMonth());
  const [view, setView] = useState("list"); // list | day
  const [selectedDate, setSelectedDate] = useState(isoToday());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [selectedCustomerLabel, setSelectedCustomerLabel] = useState("");

  // AI Route Optimizer
  const [routeOpen, setRouteOpen] = useState(false);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeData, setRouteData] = useState(null);
  const [routeDate, setRouteDate] = useState(isoToday());
  const [routeAssignee, setRouteAssignee] = useState("");

  const load = async () => {
    try {
      const [v, c] = await Promise.all([api.get(`/visits?month=${month}`), api.get("/customers")]);
      setList(v.data); setCustomers(c.data);
      if (user?.role === "admin") {
        try { setUsers((await api.get("/users")).data); } catch { /* ignore */ }
      }
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, user]);

  const openNew = (date = "") => {
    setForm({ ...blank, visit_date: date || selectedDate || isoToday(), assigned_to: user?.role === "admin" ? "" : user?.id || "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.visit_date) { toast.error("Customer + date required"); return; }
    try {
      const payload = { ...form };
      if (!payload.assigned_to) delete payload.assigned_to;
      await api.post("/visits", payload);
      toast.success("Visit planned");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const setStatus = async (v, status) => {
    try { await api.patch(`/visits/${v.id}`, { status }); load(); toast.success(`Marked ${status}`); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (v) => {
    if (!window.confirm("Delete this visit?")) return;
    try { await api.delete(`/visits/${v.id}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const optimizeRoute = async (date) => {
    setRouteDate(date || selectedDate || isoToday());
    setRouteOpen(true);
    setRouteLoading(true);
    setRouteData(null);
    try {
      const payload = { date: date || selectedDate || isoToday() };
      if (user?.role === "admin" && routeAssignee) payload.assigned_to = routeAssignee;
      const r = await api.post("/visits/optimize-route", payload);
      setRouteData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRouteLoading(false); }
  };

  // group by date for calendar view
  const byDate = list.reduce((acc, v) => {
    (acc[v.visit_date] = acc[v.visit_date] || []).push(v);
    return acc;
  }, {});
  const dates = Object.keys(byDate).sort();

  // build month calendar dates
  const [y, m] = month.split("-").map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const firstDay = new Date(y, m - 1, 1).getDay();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(`${month}-${String(d).padStart(2, "0")}`);

  return (
    <div className="p-4 lg:p-8 max-w-7xl" data-testid="visits-page">
      <PageHeader
        title="Visit Planner"
        subtitle="Plan customer visits for the month — staff route + meetings + linkup"
        action={<Button onClick={() => openNew()} data-testid="add-visit-btn"><Plus size={16}/>New Visit</Button>}
      />

      <div className="flex flex-wrap gap-2 mb-5 items-center">
        <input type="month" data-testid="month-picker" value={month} onChange={(e) => setMonth(e.target.value)} className="input-base w-auto" />
        <div className="w-px bg-[#E5E7EB] mx-1 h-6" />
        <button onClick={() => setView("list")} data-testid="view-list" className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider font-semibold ${view === "list" ? "bg-[#C1121F] text-white" : "bg-white border border-[#E5E7EB] text-[#475569]"}`}>List</button>
        <button onClick={() => setView("day")} data-testid="view-day" className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider font-semibold ${view === "day" ? "bg-[#C1121F] text-white" : "bg-white border border-[#E5E7EB] text-[#475569]"}`}>Calendar</button>
        <div className="w-px bg-[#E5E7EB] mx-1 h-6" />
        <input type="date" data-testid="route-date-picker" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="input-base w-auto" />
        <button
          onClick={() => optimizeRoute(selectedDate)}
          data-testid="ai-route-btn-top"
          className="px-3 py-1.5 rounded text-xs uppercase tracking-wider font-bold bg-gradient-to-r from-amber-500 to-[#C1121F] text-white flex items-center gap-1.5 hover:opacity-90"
        >
          <Sparkles size={13}/> AI Route
        </button>
      </div>

      {view === "day" && (
        <div className="card-flat p-3 mb-5" data-testid="month-grid">
          <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wider font-bold text-[#94A3B8] mb-1">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d} className="text-center py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              if (!d) return <div key={i} />;
              const vs = byDate[d] || [];
              const isSel = d === selectedDate;
              return (
                <button key={d} onClick={() => setSelectedDate(d)}
                  className={`p-1.5 rounded text-left min-h-[60px] transition ${isSel ? "bg-[#C1121F] text-white" : vs.length ? "bg-[#FEF2F2] hover:bg-[#FECACA]" : "hover:bg-[#F8F9FA]"}`}>
                  <div className={`text-xs font-mono-num font-semibold ${isSel ? "text-white" : "text-[#0F172A]"}`}>{Number(d.slice(8))}</div>
                  {vs.length > 0 && <div className={`text-[9px] mt-1 ${isSel ? "text-white" : "text-[#C1121F]"}`}>{vs.length} visit{vs.length > 1 ? "s" : ""}</div>}
                </button>
              );
            })}
          </div>
          <div className="mt-3 pt-3 border-t border-[#E5E7EB]">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="font-display font-bold text-sm">Visits on {selectedDate}</div>
              <div className="flex gap-2">
                <button
                  onClick={() => optimizeRoute(selectedDate)}
                  data-testid="ai-route-btn"
                  className="text-xs px-2.5 py-1 bg-gradient-to-r from-amber-500 to-[#C1121F] text-white rounded font-bold flex items-center gap-1 hover:opacity-90"
                >
                  <Sparkles size={12}/> AI Optimize Route
                </button>
                <Button variant="secondary" onClick={() => openNew(selectedDate)} className="text-xs"><Plus size={12}/> Add</Button>
              </div>
            </div>
            {(byDate[selectedDate] || []).length === 0 ? (
              <div className="text-xs text-[#94A3B8] py-3">No visits planned for this day.</div>
            ) : (
              <div className="space-y-2">
                {(byDate[selectedDate] || []).map((v) => <VisitCard key={v.id} v={v} setStatus={setStatus} remove={remove} />)}
              </div>
            )}
          </div>
        </div>
      )}

      {view === "list" && (
        list.length === 0 ? (
          <EmptyState icon={MapPin} title="No visits planned" hint="Plan customer visits for the month — staff visits, follow-ups, deliveries."
            action={<Button onClick={() => openNew()}><Plus size={16}/> First Visit</Button>} />
        ) : (
          <div className="space-y-4" data-testid="visits-list">
            {dates.map((d) => (
              <div key={d}>
                <div className="flex items-center gap-2 mb-2 text-sm font-display font-bold">
                  <Calendar size={14} className="text-[#C1121F]"/>
                  {new Date(d).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                  <span className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">· {byDate[d].length} visit{byDate[d].length > 1 ? "s" : ""}</span>
                </div>
                <div className="space-y-2">
                  {byDate[d].map((v) => <VisitCard key={v.id} v={v} setStatus={setStatus} remove={remove} />)}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      <Modal open={routeOpen} onClose={() => setRouteOpen(false)} title={`AI Optimized Route — ${routeDate}`} maxWidth="max-w-2xl">
        <div className="space-y-3" data-testid="route-modal">
          {routeLoading && (
            <div className="py-8 text-center text-[#475569] flex flex-col items-center gap-2">
              <RefreshCw className="animate-spin text-[#C1121F]" size={28}/>
              <div className="text-sm">Gemini AI is planning the optimal route…</div>
            </div>
          )}
          {!routeLoading && routeData && (
            <>
              <div className="p-3 bg-gradient-to-r from-amber-50 to-red-50 border border-amber-200 rounded">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles size={14} className="text-[#C1121F]"/>
                  <span className="text-[10px] uppercase tracking-wider font-bold text-[#C1121F]">
                    {routeData.ai_used ? "AI Recommendation" : "Smart Route"}
                  </span>
                  <span className="text-[10px] text-[#94A3B8] ml-auto">{routeData.total_stops} stops</span>
                </div>
                <div className="text-xs text-[#0F172A]" data-testid="route-narrative">{routeData.narrative}</div>
              </div>

              {routeData.total_stops === 0 ? (
                <div className="text-center text-sm text-[#94A3B8] py-6">No planned visits for this date. Add some visits first.</div>
              ) : (
                <div className="space-y-2" data-testid="route-stops">
                  {routeData.optimized.map((stop) => (
                    <div key={stop.visit_id} className="card-flat p-3 flex items-center gap-3" data-testid={`route-stop-${stop.stop_number}`}>
                      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#C1121F] text-white flex items-center justify-center font-bold font-mono-num">
                        {stop.stop_number}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-display font-bold text-sm">{stop.customer_name}</span>
                          {stop.visit_time && <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded font-mono-num font-bold">{stop.visit_time}</span>}
                          {stop.tier && <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded">{stop.tier}</span>}
                        </div>
                        <div className="text-xs text-[#475569] mt-0.5 flex items-center gap-1">
                          <MapPin size={10}/>{stop.city || "—"}{stop.address ? ` · ${stop.address}` : ""}
                        </div>
                        {stop.purpose && <div className="text-[11px] text-[#0F172A] mt-0.5">📌 {stop.purpose}</div>}
                        <div className="text-[10px] text-[#94A3B8] mt-0.5 font-mono-num">
                          LTV ₹{Math.round(stop.total_spent || 0).toLocaleString("en-IN")} · Credit {stop.credit_score}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        {stop.maps_url && (
                          <a href={stop.maps_url} target="_blank" rel="noreferrer" className="p-1.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100" title="Open in Maps">
                            <Navigation size={12}/>
                          </a>
                        )}
                        {stop.phone && (
                          <a href={`tel:${stop.phone}`} className="p-1.5 rounded bg-green-50 text-green-700 hover:bg-green-100" title="Call">
                            <Phone size={12}/>
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="pt-2 flex justify-end gap-2">
                <Button variant="secondary" onClick={() => optimizeRoute(routeDate)} data-testid="reoptimize-btn">
                  <RefreshCw size={12}/> Re-optimize
                </Button>
                <Button onClick={() => setRouteOpen(false)}>Close</Button>
              </div>
            </>
          )}
        </div>
      </Modal>

      <Modal open={open} onClose={() => setOpen(false)} title="Plan Visit" maxWidth="max-w-xl">
        <form onSubmit={save} className="space-y-3">
          <Field label="Customer" required>
            <Typeahead
              loadOptions={searchCustomersAsync}
              value={form.customer_id}
              selectedLabel={selectedCustomerLabel}
              onChange={(id, opt) => { setForm({...form, customer_id: id}); setSelectedCustomerLabel(opt?.name || ""); }}
              placeholder="Type to search customer…"
              required
              testid="visit-customer"
              renderMeta={(c) => `${c.type || ""} ${c.city ? "· " + c.city : ""} ${c.phone ? "· " + c.phone : ""}`}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Visit Date" required>
              <input data-testid="visit-date" required type="date" value={form.visit_date} onChange={(e) => setForm({...form, visit_date: e.target.value})} className="input-base" />
            </Field>
            <Field label="Time">
              <input data-testid="visit-time" type="time" value={form.visit_time} onChange={(e) => setForm({...form, visit_time: e.target.value})} className="input-base" />
            </Field>
          </div>
          <Field label="Purpose">
            <input data-testid="visit-purpose" value={form.purpose} onChange={(e) => setForm({...form, purpose: e.target.value})} className="input-base" placeholder="e.g. Sample show + new product pitch" />
          </Field>
          {user?.role === "admin" && (
            <Field label="Assign To">
              <select data-testid="visit-assignee" value={form.assigned_to} onChange={(e) => setForm({...form, assigned_to: e.target.value})} className="input-base">
                <option value="">Self ({user.name})</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
          )}
          <Field label="Notes">
            <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="input-base" rows={2} />
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="save-visit-btn">Plan Visit</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function VisitCard({ v, setStatus, remove }) {
  const meta = statusMeta[v.status] || statusMeta.planned;
  return (
    <div className="card-flat p-3 flex items-start gap-3" data-testid={`visit-${v.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display font-bold text-sm">{v.customer_name}</span>
          {v.visit_time && <span className="text-xs text-[#475569] font-mono-num">{v.visit_time}</span>}
          <span className={`badge ${meta.cls}`}>{meta.label}</span>
        </div>
        {v.customer_city && <div className="text-xs text-[#475569] mt-0.5 flex items-center gap-1"><MapPin size={10}/> {v.customer_city}{v.customer_address ? ` · ${v.customer_address}` : ""}</div>}
        {v.purpose && <div className="text-xs text-[#0F172A] mt-1">📌 {v.purpose}</div>}
        {v.assignee_name && v.assignee_name !== "Unassigned" && <div className="text-[10px] text-[#94A3B8] mt-1">👤 {v.assignee_name}</div>}
      </div>
      {v.status === "planned" && (
        <div className="flex flex-col gap-1">
          <button onClick={() => setStatus(v, "done")} data-testid={`visit-done-${v.id}`} className="p-1.5 rounded bg-green-100 text-green-700 hover:bg-green-200"><Check size={12}/></button>
          <button onClick={() => setStatus(v, "missed")} data-testid={`visit-miss-${v.id}`} className="p-1.5 rounded bg-red-50 text-red-700 hover:bg-red-100"><X size={12}/></button>
        </div>
      )}
      {v.customer_phone && <a href={`tel:${v.customer_phone}`} className="p-1.5 text-[#C1121F] hover:bg-[#FEF2F2] rounded"><Phone size={13}/></a>}
      <button onClick={() => remove(v)} className="text-[#94A3B8] hover:text-red-600 p-1.5"><Trash2 size={12}/></button>
    </div>
  );
}
